import { validSeoPath } from 'shared/util/formatting';
import type { DestinationItem } from '@/components/internalLinks/ExpandableDestinationList';
import flight from './flight.json';
import hotel from './hotel.json';
import { InternalLinksSection } from './InternalLinksSection';
import prepackage from './prepackage.json';

const makeValidPath = (pathElements: (string | undefined)[]) =>
    pathElements
        .filter((pathElement) => pathElement !== undefined)
        .map((pathElement) => validSeoPath(pathElement))
        .join('/');

type FlightData = typeof flight;
type DestinationData = typeof hotel | typeof prepackage;

const transformFlightDestinations = (data: FlightData): DestinationItem[] =>
    Object.entries(data).map(([toCity, cityData]) => ({
        title: `Fly til ${toCity}`,
        links: cityData.fromCities.map((fromCity) => ({
            label: `Fly fra ${fromCity} til ${toCity}`,
            href: `/reise/flybilletter/norge/${makeValidPath([fromCity, cityData.country, toCity])}/`,
        })),
    }));

const transformDestinations = (data: DestinationData, pathPrefix: string): DestinationItem[] =>
    Object.entries(data).map(([toPlace, destinations]) => ({
        title: toPlace,
        links: destinations.map((dest) => {
            const country = typeof dest === 'string' ? toPlace : dest.country;
            const city = typeof dest === 'string' ? dest : dest.city;
            return {
                label: city || country,
                href: `/reise/${makeValidPath([pathPrefix, country, city])}/`,
            };
        }),
    }));

export const InternalLinks = () => {
    const flightItems = transformFlightDestinations(flight);
    const prepackageItems = transformDestinations(prepackage, 'pakkereiser');
    const hotelItems = transformDestinations(hotel, 'hotell');

    return (
        <>
            <InternalLinksSection title="Finn flyreiser til populære byer" titleId="seo-flights-to-popular-cities" items={flightItems} />
            <InternalLinksSection
                title="Finn pakkereiser til spennende steder"
                titleId="seo-pp-to-exciting-places"
                items={prepackageItems}
            />
            <InternalLinksSection title="Finn rimelige hoteller i Europa" titleId="seo-cheap-hotels-in-europe" items={hotelItems} />
        </>
    );
};
